import { Button, Group, Select, Stack, TextInput, Title } from "@mantine/core";
import type { FormArrayElement, UseFormReturnType } from "@mantine/form";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { getSchoolOptions, type School } from "../../models/school";
import { emptySchoolDay } from "../../models/schoolDay";
import { WeekdayMultiSelect } from "../select/WeekdayMultiSelect";

export type SchoolDayFormType = {
  school_days_attributes: {
    id: number | null;
    days: string[];
    school_id?: string;
    class_name: string;
  }[];
};

export type SchoolDayError = {
  school?: string[];
};

type SchoolDayFormProps<T extends SchoolDayFormType> = {
  form: UseFormReturnType<T>;
  maxDays?: number;
  errors?: SchoolDayError[];
  schools: School[];
};

export function SchoolDayForm<T extends SchoolDayFormType>({ schools, form, maxDays = 2, errors }: SchoolDayFormProps<T>) {
  const schoolDaysLength = form.getValues().school_days_attributes.length;

  const addSchool = () => form.insertListItem("school_days_attributes", emptySchoolDay as FormArrayElement<T, "school_days_attributes">);
  const removeSchool = () => form.removeListItem("school_days_attributes", schoolDaysLength - 1);

  return (
    <Stack>
      {form.getValues().school_days_attributes.map((day, index) => (
        <Stack key={day.id ?? `s${index}`} mb="md">
          <Title mb="0" order={3}>{`Schule ${index + 1}`}</Title>
          <Group>
            <Select
              maxDropdownHeight="15rem"
              flex={2}
              withAsterisk
              label="Schule"
              data={getSchoolOptions(schools)}
              key={form.key(`school_days_attributes.${index}.school_id`)}
              {...form.getInputProps(`school_days_attributes.${index}.school_id`)}
              error={errors?.[index]?.school}
            />
            <TextInput
              flex={2}
              label="Klasse"
              key={form.key(`school_days_attributes.${index}.class_name`)}
              {...form.getInputProps(`school_days_attributes.${index}.class_name`)}
            />
          </Group>
          <WeekdayMultiSelect
            label="Schultage"
            clearable
            placeholder="Wähle Schultage"
            key={form.key(`school_days_attributes.${index}.days`)}
            {...form.getInputProps(`school_days_attributes.${index}.days`)}
          />
          {schoolDaysLength - 1 === index && (
            <Button leftSection={<IconMinus />} variant="subtle" onClick={removeSchool}>
              Schule entfernen
            </Button>
          )}
          {schoolDaysLength < maxDays && schoolDaysLength - 1 === index && (
            <Button leftSection={<IconPlus />} variant="subtle" onClick={addSchool}>
              Schule hinzufügen
            </Button>
          )}
        </Stack>
      ))}

      {schoolDaysLength === 0 && (
        <Button leftSection={<IconPlus />} variant="subtle" onClick={addSchool}>
          Schule hinzufügen
        </Button>
      )}
    </Stack>
  );
}
